/*
  Safir storefront behavior for the sx-* sections. Plain deferred script,
  no dependencies. Every feature is a progressive enhancement: links,
  forms and native <details>/<dialog> keep working without it.

  - Reveal on scroll ([data-sx-reveal])
  - Announcement rotator ([data-sx-announce])
  - Tabs ([data-sx-tabs])
  - Product card pack picker ([data-sx-card])
  - 18+ confirmation before creatine products go in the cart (#sx-age)
  - Auto-submitting selects ([data-sx-autosubmit])

  Add to cart itself is the theme's product-form-component; this file only
  keeps its hidden variant input in sync and gates it behind the age check.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- Reveal on scroll ------------------------------------------------- */

  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('[data-sx-reveal]:not(.is-in)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || reduceMotion.matches) {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    // Anything already on screen is shown in the same task the class lands
    // in, so above-the-fold content never flashes out and back in.
    var fold = window.innerHeight;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    items.forEach(function (el) {
      if (el.getBoundingClientRect().top < fold) {
        el.classList.add('is-in');
      } else {
        observer.observe(el);
      }
    });
  }

  /* ---- Announcement rotator --------------------------------------------- */

  function initAnnounce(el) {
    if (el.dataset.sxReady) return;
    el.dataset.sxReady = 'true';

    var msgs = Array.prototype.slice.call(el.querySelectorAll('.sx-announce__msg'));
    if (msgs.length < 2) return;

    var index = 0;
    var timer = null;
    var delay = (Number(el.dataset.speed) || 5) * 1000;

    function show(next) {
      index = (next + msgs.length) % msgs.length;
      msgs.forEach(function (m, i) {
        var active = i === index;
        m.classList.toggle('is-active', active);
        m.setAttribute('aria-hidden', active ? 'false' : 'true');
        m.querySelectorAll('a').forEach(function (a) {
          a.tabIndex = active ? 0 : -1;
        });
      });
    }
    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }
    function start() {
      stop();
      if (reduceMotion.matches) return;
      timer = setInterval(function () {
        show(index + 1);
      }, delay);
    }

    var prev = el.querySelector('[data-sx-prev]');
    var next = el.querySelector('[data-sx-next]');
    if (prev) {
      prev.hidden = false;
      prev.addEventListener('click', function () {
        show(index - 1);
        start();
      });
    }
    if (next) {
      next.hidden = false;
      next.addEventListener('click', function () {
        show(index + 1);
        start();
      });
    }

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);
    el.addEventListener('focusin', stop);
    el.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    show(0);
    start();
  }

  /* ---- Tabs ------------------------------------------------------------- */

  function initTabs(el) {
    if (el.dataset.sxReady) return;
    el.dataset.sxReady = 'true';

    var tabs = Array.prototype.slice.call(el.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !on;
          if (on) initReveal(panel);
        }
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(tab, false);
      });
      tab.addEventListener('keydown', function (event) {
        var to = null;
        if (event.key === 'ArrowRight') to = tabs[(i + 1) % tabs.length];
        if (event.key === 'ArrowLeft') to = tabs[(i - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') to = tabs[0];
        if (event.key === 'End') to = tabs[tabs.length - 1];
        if (!to) return;
        event.preventDefault();
        select(to, true);
      });
    });
  }

  /* ---- Product card pack picker ----------------------------------------- */

  function initCard(card) {
    if (card.dataset.sxReady) return;
    card.dataset.sxReady = 'true';

    var idInput = card.querySelector('form input[name="id"]');
    var price = card.querySelector('[data-sx-price]');
    var per = card.querySelector('[data-sx-per]');
    var button = card.querySelector('form button[type="submit"]');
    var label = button && button.querySelector('.add-to-cart-text__content span span');

    card.addEventListener('change', function (event) {
      var input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-sx-size]')) return;

      if (idInput) idInput.value = input.value;
      if (price && input.dataset.priceHtml) price.innerHTML = input.dataset.priceHtml;
      if (per) per.textContent = input.dataset.per || '';

      var available = input.dataset.available === 'true';
      if (button) button.disabled = !available;
      if (label) label.textContent = available ? card.dataset.addLabel || label.textContent : card.dataset.soldLabel || 'Sold out';
    });
  }

  /* ---- 18+ confirmation ------------------------------------------------- */

  var age = { ok: false, variants: null, dialog: null, pending: null };

  function ageConfig() {
    if (age.variants) return age.variants;
    var data = document.getElementById('sx-age-data');
    age.variants = {};
    if (!data) return age.variants;
    try {
      var parsed = JSON.parse(data.textContent);
      (parsed.variants || []).forEach(function (id) {
        age.variants[String(id)] = true;
      });
    } catch (e) {}
    return age.variants;
  }

  function ageConfirmed() {
    if (age.ok) return true;
    try {
      return window.localStorage.getItem('sx-age-ok') === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberAge() {
    age.ok = true;
    try {
      window.localStorage.setItem('sx-age-ok', '1');
    } catch (e) {}
  }

  function needsAge(form) {
    if (!form || ageConfirmed()) return false;
    var id = form.querySelector('input[name="id"]');
    return !!(id && ageConfig()[String(id.value)]);
  }

  function recordAgeOnCart() {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(base + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { 'Age confirmed (18+)': 'Yes' } }),
    }).catch(function () {});
  }

  function openAge(button) {
    var dialog = age.dialog || document.getElementById('sx-age');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    age.dialog = dialog;
    age.pending = button;
    dialog.showModal();
    return true;
  }

  function initAge() {
    var dialog = document.getElementById('sx-age');
    if (!dialog) return;
    age.dialog = dialog;

    dialog.addEventListener('click', function (event) {
      var action = event.target.closest && event.target.closest('[data-sx-age]');
      if (!action) {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === dialog) dialog.close('cancel');
        return;
      }
      if (action.dataset.sxAge === 'confirm') {
        rememberAge();
        var button = age.pending;
        age.pending = null;
        dialog.close('confirm');
        recordAgeOnCart().then(function () {
          if (button && button.isConnected) button.click();
        });
      } else {
        age.pending = null;
        dialog.close('cancel');
      }
    });

    // Capture phase on window runs before the theme's own click and submit
    // handlers, so an unconfirmed add never reaches the cart.
    window.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest && event.target.closest('button[type="submit"], input[type="submit"]');
        if (!button || !button.form) return;
        if (!/\/cart\/add/.test(button.form.getAttribute('action') || '')) return;
        if (!needsAge(button.form)) return;
        if (!openAge(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
    window.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
        if (!needsAge(form)) return;
        var button = form.querySelector('button[type="submit"]');
        if (!openAge(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  /* ---- Auto-submitting selects ------------------------------------------ */

  function initAutosubmit() {
    document.addEventListener('change', function (event) {
      var el = event.target;
      if (el && el.matches && el.matches('[data-sx-autosubmit]') && el.form) el.form.submit();
    });
  }

  /* ---- Boot ------------------------------------------------------------- */

  function initAll(scope) {
    var s = scope || document;
    s.querySelectorAll('[data-sx-announce]').forEach(initAnnounce);
    s.querySelectorAll('[data-sx-tabs]').forEach(initTabs);
    s.querySelectorAll('[data-sx-card]').forEach(initCard);
    initReveal(s);
    s.querySelectorAll('[data-sx-hero]').forEach(function (hero) {
      requestAnimationFrame(function () {
        hero.classList.add('is-in');
      });
    });
  }

  root.classList.add('sx-js');
  initAll(document);
  initAge();
  initAutosubmit();

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
